import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sourceSelect", "targetSelect", "message"]

  // CSS class constants for message styling
  static WARNING_CLASSES = "p-3 rounded-lg border border-pills-status-warning-border dark:border-pills-status-warning-border-dark bg-banner-warning-bg dark:bg-banner-warning-bg-dark text-semantics-text-primary dark:text-semantics-text-primary-dark"
  static SUCCESS_CLASSES = "p-3 rounded-lg border border-pills-status-success-border dark:border-pills-status-success-border-dark bg-banner-success-bg dark:bg-banner-success-bg-dark text-semantics-text-primary dark:text-semantics-text-primary-dark"

  /**
   * Validates that selected source and target organizations have matching domains
   * Domain data format: { orgId: [[domain, count], [domain, count], ...] }
   */
  validateDomains() {
    const sourceId = this.sourceSelectTarget.value
    const targetId = this.targetSelectTarget.value

    // Clear message if either dropdown is empty
    if (!sourceId || !targetId) {
      this.clearMessage()
      return
    }

    // Get domain data from data attributes with error handling
    let sourceDomains, targetDomains
    try {
      sourceDomains = JSON.parse(this.sourceSelectTarget.dataset.domains)
      targetDomains = JSON.parse(this.targetSelectTarget.dataset.domains)
    } catch (error) {
      console.error("Failed to parse domain data:", error)
      this.clearMessage()
      return
    }

    // Get domains for selected organizations
    const selectedSourceDomains = sourceDomains[sourceId] || []
    const selectedTargetDomains = targetDomains[targetId] || []

    // Find matching domains
    const matchingDomains = this.findMatchingDomains(
      selectedSourceDomains,
      selectedTargetDomains
    )

    // Display appropriate message
    this.displayValidationMessage(
      matchingDomains,
      selectedSourceDomains,
      selectedTargetDomains
    )
  }

  clearMessage() {
    this.messageTarget.innerHTML = ""
  }

  /**
   * Finds domains that exist in both source and target
   * @param {Array<[string, number]>} sourceDomains - Array of [domain, count] tuples
   * @param {Array<[string, number]>} targetDomains - Array of [domain, count] tuples
   * @returns {Array<string>} Array of matching domain names
   */
  findMatchingDomains(sourceDomains, targetDomains) {
    const targetDomainNames = targetDomains.map(([domain]) => domain)
    return sourceDomains
      .map(([domain]) => domain)
      .filter(domain => targetDomainNames.includes(domain))
  }

  displayValidationMessage(matchingDomains, sourceDomains, targetDomains) {
    if (matchingDomains.length === 0) {
      this.messageTarget.innerHTML = this.buildWarningMessage()
    } else {
      this.messageTarget.innerHTML = this.buildMatchMessage(
        matchingDomains,
        sourceDomains,
        targetDomains
      )
    }
  }

  buildWarningMessage() {
    return `
      <div class="${this.constructor.WARNING_CLASSES}">
        <strong>Verification Warning:</strong><br>
        No matching domains found, please verify you have selected the correct organizations
      </div>
    `
  }

  buildMatchMessage(matchingDomains, sourceDomains, targetDomains) {
    const sourceDomainsMap = new Map(sourceDomains)
    const targetDomainsMap = new Map(targetDomains)

    // Calculate total identities for matching domains
    let totalSourceIdentities = 0
    let totalTargetIdentities = 0

    matchingDomains.forEach(domain => {
      totalSourceIdentities += sourceDomainsMap.get(domain) || 0
      totalTargetIdentities += targetDomainsMap.get(domain) || 0
    })

    const matchWord = matchingDomains.length === 1 ? "match" : "matches"
    const identityWord = totalSourceIdentities === 1 ? "identity" : "identities"

    return `
      <div class="${this.constructor.SUCCESS_CLASSES}">
        <strong>Verification Match:</strong><br>
        Found ${matchingDomains.length} domain ${matchWord} between the selected organizations, with a total of ${totalSourceIdentities} ${identityWord} in the source corresponding to ${totalTargetIdentities} in the target.
      </div>
    `
  }
}
